/**
 * The things this service talks to, one entry each, built from GET /settings.
 *
 * A connection is what a person configures: where it is and the key for it.
 * The service holds those as separate lists (lanes, addresses, credentials,
 * sources, switches). This puts each connection's URL and key back together,
 * so the page never shows an address in one place and its key in another.
 */

import type { AuthorityEndpoint, Settings } from '../api'
import { formatTimestamp } from '../platform'

export type ConnectionId =
  | 'tim'
  | 'ocpi'
  | 'hubject'
  | 'hub_assets'
  | 'hub_timeline'
  | 'hub_events'

export const FORWARD = 'hubject_forward_enabled'
export const ASK = 'hubject_ask_enabled'
export const SHADOW = 'hubject_shadow_enabled'
export const LOOKUP = 'membership_lookup_enabled'
export const TEST_CALL = 'hubject_dial_once_enabled'
export const UNBILLED = 'unbilled_events_enabled'
export const PRE_AUTHORIZATION = 'pre_authorization_enabled'

export interface UrlField {
  value: string
  /** console, deployment or not set. */
  source: string
  /** The `addresses` name to save under. Absent means set by the deployment. */
  address?: string
  help: string
  /** Shown last in the field's line, after anything the form adds. */
  example?: string
}

export interface KeyField {
  label: 'API key' | 'Bearer token'
  /** The name the key is saved and tested under. */
  relationship: string
  isSet: boolean
  source: string
  updatedAt: number | null
  help: string
}

export interface LinkField {
  setting: 'tim_console_url' | 'ocpi_dashboard_url'
  label: string
  value: string
  help: string
}

export interface Connection {
  id: ConnectionId
  name: string
  url: UrlField | null
  key: KeyField | null
  link?: LinkField
  /** The lane behind it, when the service reports one. */
  lane?: AuthorityEndpoint
  /** What Test does: present the key, ask for health, both, or nothing. */
  test: 'key' | 'health' | 'both' | 'none'
  /** One line, for a connection with nothing to set or test. */
  note?: string
}

const KEEP_KEY = 'Leave empty to keep the current one.'

function addressSource(inUseFrom: string | undefined): string {
  if (inUseFrom === 'typed into the console') return 'console'
  if (inUseFrom === 'the deployment') return 'deployment'
  return 'not set'
}

/** `unreadable` is not `not set`: something is stored and cannot be used. */
export function keySource(inEffect: string | undefined, isSet: boolean): string {
  if (inEffect === 'stored') return 'console'
  if (inEffect === 'deployment') return 'deployment'
  if (inEffect === 'unreadable') return 'console, cannot be decrypted'
  if (inEffect === 'none') return 'not set'
  return isSet ? 'set' : 'not set'
}

function keyLabel(scheme: string | undefined): KeyField['label'] {
  return scheme === 'bearer' ? 'Bearer token' : 'API key'
}

export function connectionsFrom(data: Settings): Connection[] {
  const lane = (name: string) => (data.authorities ?? []).find((a) => a.authority === name)
  const address = (name: string) => (data.addresses ?? []).find((a) => a.name === name)
  const credential = (name: string) =>
    (data.credentials ?? []).find((c) => c.authority === name)
  const source = (name: string) => (data.sources ?? []).find((s) => s.source === name)

  const deployed = (found: AuthorityEndpoint | undefined, variable: string): UrlField | null =>
    found
      ? {
          value: found.url,
          source: found.url ? 'deployment' : 'not set',
          help: `Set by the deployment (${variable}). Not editable here.`,
        }
      : null

  const typed = (name: string, help: string, example: string): UrlField | null => {
    const found = address(name)
    return found
      ? {
          value: found.value,
          source: addressSource(found.in_use_from),
          address: name,
          help,
          example,
        }
      : null
  }

  const laneKey = (name: string, help: string): KeyField | null => {
    const found = credential(name)
    return found
      ? {
          label: keyLabel(found.scheme),
          relationship: name,
          isSet: found.is_set,
          source: keySource(found.in_effect, found.is_set),
          updatedAt: found.updated_at,
          help: `${help} ${KEEP_KEY}`,
        }
      : null
  }

  const hubToken = source('hub_assets')

  return [
    {
      id: 'tim',
      name: 'TIM',
      url: deployed(lane('tim'), 'TIM_URL'),
      key: laneKey(
        'tim',
        'The shared secret Service TIM expects. Value: Key Vault secret tim-m2m-token of this environment.',
      ),
      link: {
        setting: 'tim_console_url',
        label: 'Console URL',
        value: data.tim_console_url ?? '',
        help: 'Address of the TIM console, for the link on a decision. Example: https://tim.example.com',
      },
      lane: lane('tim'),
      test: 'health',
    },
    {
      id: 'ocpi',
      name: 'Service OCPI',
      url: deployed(lane('ocpi'), 'OCPI_URL'),
      key: laneKey(
        'ocpi',
        'The Broker API Key of Service OCPI. Copy it from the OCPI dashboard, Settings, Connections, field "Broker API Key".',
      ),
      link: {
        setting: 'ocpi_dashboard_url',
        label: 'Dashboard URL',
        value: data.ocpi_dashboard_url ?? '',
        help: 'Address of the Service OCPI dashboard, for the link on a decision. Example: https://ocpi.example.com',
      },
      lane: lane('ocpi'),
      test: 'both',
    },
    {
      id: 'hubject',
      name: 'Hubject connector (bridge)',
      url: typed(
        'hubject_bridge_url',
        'Base URL of the connector, ending in /v2.',
        'https://hubject.wallefleet.de/v2',
      ),
      key: laneKey(
        'hubject',
        'Issued on the connector’s Settings page, button "Issue or regenerate EAS key".',
      ),
      test: 'key',
    },
    {
      id: 'hub_assets',
      name: 'Integration Hub: station list',
      url: typed(
        'hub_assets_url',
        'Base URL of the Integration Hub, without a path.',
        'https://hub.example.com',
      ),
      key: hubToken
        ? {
            label: keyLabel(hubToken.scheme),
            relationship: 'hub_assets',
            isSet: hubToken.is_set,
            source: keySource(hubToken.in_effect, hubToken.is_set),
            updatedAt: hubToken.updated_at,
            help: `Issued by running scripts/seed-ea-token.sh in the Hub repository. 64 hexadecimal characters. Enter the token only, without the word Bearer. ${KEEP_KEY}`,
          }
        : null,
      test: 'key',
    },
    {
      id: 'hub_timeline',
      name: 'Integration Hub: transaction timeline',
      url: null,
      key: null,
      test: 'none',
      note: 'Not used yet. It will use the same token as the station list.',
    },
    {
      id: 'hub_events',
      name: 'Integration Hub: events',
      url: deployed(lane('hub'), 'HUB_URL'),
      key: null,
      lane: lane('hub'),
      test: 'none',
      note: 'No key: events are signed. No test: the Hub has no health check for events.',
    },
  ]
}

/** What POST /credentials/{name}/verify answers. */
export interface KeyTest {
  key: string
  reachable: boolean
  detail: string
  status_code?: number | null
  elapsed_ms?: number | null
}

/** What POST /authorities/{name}/test answers. */
export interface HealthTest {
  status: string
  detail: string | null
  latency_ms: number
}

export interface TestLine {
  ok: boolean
  line: string
  /** The key was presented and the connection answered about it. */
  presented?: boolean
}

const took = (ms: number | null | undefined) => (typeof ms === 'number' ? ` in ${ms} ms` : '')

/** One line saying what a key test proved. `what` is "key" or "token". */
export function keyTestLine(found: KeyTest, what: string): TestLine {
  const named = what.charAt(0).toUpperCase() + what.slice(1)
  switch (found.key) {
    case 'accepted':
      return { ok: true, presented: true, line: `${named} accepted${took(found.elapsed_ms)}.` }
    case 'refused':
      return {
        ok: false,
        presented: true,
        line: `${found.status_code ?? 401}: the ${what} was refused.`,
      }
    case 'no-key':
      return { ok: false, line: `No ${what} is set.` }
    case 'unreachable':
      return {
        ok: false,
        line:
          typeof found.elapsed_ms === 'number'
            ? `No answer (failed after ${found.elapsed_ms} ms).`
            : 'No answer.',
      }
    default:
      return {
        ok: false,
        line: found.status_code
          ? `HTTP ${found.status_code}${took(found.elapsed_ms)}. This does not prove the ${what}: ${found.detail}.`
          : `Not tested: ${found.detail}.`,
      }
  }
}

/** One line for a health check, which presents no key. */
export function healthTestLine(found: HealthTest): TestLine {
  if (found.status === 'ok') {
    return {
      ok: true,
      line: `Reachable in ${found.latency_ms} ms. This test does not present the key.`,
    }
  }
  return {
    ok: false,
    line: `${found.status} after ${found.latency_ms} ms${found.detail ? `: ${found.detail}` : ''}.`,
  }
}

/** What GET /catalogue/sync answers. */
export interface StationListSync {
  configured: boolean
  running: boolean
  attempted_at: string | null
  pulled_at: string | null
  /** The last attempt replaced the list. */
  ok: boolean
  last_error: string | null
  stations: number | null
}

/** The last sync of the station list, in one line. */
export function syncLine(state: StationListSync): TestLine {
  if (state.running) return { ok: true, line: 'Sync running.' }
  if (!state.attempted_at) return { ok: true, line: 'No sync yet.' }
  const when = formatTimestamp(state.attempted_at)
  if (state.ok) {
    const kept = state.stations ?? 0
    return {
      ok: true,
      line: `Last sync ${when}, OK, ${kept.toLocaleString('en-GB')} ${kept === 1 ? 'station' : 'stations'}`,
    }
  }
  return {
    ok: false,
    line: `Last sync ${when}, failed: ${state.last_error ?? 'no reason recorded'}`,
  }
}

/** The console itself failing to ask says nothing about the connection. */
export function askFailedLine(message: string): TestLine {
  return { ok: false, line: `The console could not ask: ${message}` }
}
